/**
 * Reading TiledMedia's answer for one feed.
 *
 * Shared by the Test lab and Simulive panels because the awkward parts are the
 * same in both: TM returns several configs per feed, keeps a dead stack's uuid,
 * and keeps a stale failure on a config that has since relaunched fine. Two
 * copies of these rules would drift, and each one is a wrong colour on screen.
 */

/** The fields any TM-derived stack row has, whichever endpoint returned it. */
export interface TmStackLike {
  /** TM's own state string, or `unknown` when it reported none. */
  state: string;
  /**
   * The most recent stack TM ran for this config, or null if it never ran one.
   * The *last* stack, not necessarily a live one.
   */
  stackUuid: string | null;
  deploymentFailure: string | null;
}

/**
 * Whether TM reports this stack as anything other than shut down.
 *
 * A row existing is NOT enough: TM keeps returning a config after its stack has
 * died, so a run from last week still appears in the list. Only the state says
 * whether something is live. Deliberately inclusive of `error`, so a failed
 * launch still counts as something that needs tearing down.
 */
export const isTmStackActive = (state?: string): boolean =>
  !!state && state !== 'inactive' && state !== 'unknown';

export interface ResolvedTmStack<T extends TmStackLike> {
  /** The config entry that answers for this feed, or null if TM has none. */
  entry: T | null;
  /**
   * Three genuinely different outcomes, which must not render the same:
   *
   * - `null`     TM has no config — this feed has never been launched.
   * - `finished` inactive, but it has a stack uuid, so it ran and stopped.
   * - anything else is TM's own state, verbatim.
   */
  state: string | null;
  /** Suppressed while running; see below. */
  failure: string | null;
}

/**
 * Pick the one config entry that answers for a feed.
 *
 * TM creates a config per launch instead of upserting by name, so a feed that
 * has run four times has four entries — verified against a live /list. Prefer a
 * running one; otherwise the first leftover stands in.
 *
 * State and failure come back together, from that one chosen entry, rather than
 * through separate lookups: with duplicate names, searching twice can pair one
 * config's state with another's error.
 */
export function resolveTmStack<T extends TmStackLike>(
  entries: T[],
): ResolvedTmStack<T> {
  const preferred = entries.find((entry) => isTmStackActive(entry.state));
  const entry = preferred ?? entries[0];

  if (!entry) {
    return { entry: null, state: null, failure: null };
  }

  // Idle with a uuid means TM ran this feed and it ended; idle without one means
  // the config exists but never launched.
  const state =
    !preferred && entry.state === 'inactive' && entry.stackUuid
      ? 'finished'
      : entry.state;

  return {
    entry,
    state,
    // Hidden on a running feed. TM keeps the last failure on a config that has
    // since relaunched successfully — the same trap as `stackUuid` — and beside
    // a green status a stale error reads as a live one.
    failure: state === 'running' ? null : entry.deploymentFailure,
  };
}
